import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';

type RefreshContextType = {
  isManualRefreshing: boolean;
  isRefreshing: boolean;
  manualRefresh: () => void;
  autoRefresh: () => void;
  isAutoRefreshing: boolean;
  /**
   * Registers a callback to be called when a refresh occurs (when `manualRefresh` or `autoRefresh` are called).
   */
  subscribe: (callback: () => void) => void;
  /**
   * Unregisters a callback that was registered with `subscribe`. Must be called on component unmount (that has registered callbacks) to avoid unnecessary request invalidation.
   */
  unsubscribe: (callback: () => void) => void;
};

const RefreshContext = createContext<RefreshContextType>({
  autoRefresh: () => {},
  manualRefresh: () => {},
  isRefreshing: false,
  isManualRefreshing: false,
  isAutoRefreshing: false,
  subscribe: () => {},
  unsubscribe: () => {},
});

const RefreshContextProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const listeners = useRef<(() => void)[]>([]);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);

  const subscribe = useCallback((callback: () => void) => {
    listeners.current.push(callback);
  }, []);

  const unsubscribe = useCallback((callback: () => void) => {
    listeners.current = listeners.current.filter((cb) => cb !== callback);
  }, []);

  const notifyListeners = useCallback(() => {
    listeners.current.forEach((callback) => callback());
  }, []);

  const manualRefresh = useCallback(() => {
    setIsManualRefreshing(true);
    setTimeout(() => {
      setIsManualRefreshing(false);
    }, 500);

    notifyListeners();
  }, [notifyListeners]);

  const autoRefresh = useCallback(() => {
    setIsAutoRefreshing(true);
    setTimeout(() => {
      setIsAutoRefreshing(false);
    }, 500);

    notifyListeners();
  }, [notifyListeners]);

  return (
    <RefreshContext.Provider
      value={{
        manualRefresh,
        autoRefresh,
        isRefreshing: isManualRefreshing || isAutoRefreshing,
        isManualRefreshing,
        isAutoRefreshing,
        subscribe,
        unsubscribe,
      }}
    >
      {children}
    </RefreshContext.Provider>
  );
};

const useRefreshContext = () => {
  return useContext(RefreshContext);
};

/**
 * Registers callbacks to be called when a refresh occurs (when `manualRefresh` or `autoRefresh` from `RefreshContext` are called).
 *
 * Unregisters the callbacks on component unmount.
 */
export const useRefreshCallbacks = (...callbacks: (() => void)[]) => {
  const { subscribe, unsubscribe, isAutoRefreshing, isManualRefreshing, isRefreshing } = useRefreshContext();

  useEffect(() => {
    callbacks.forEach((callback) => subscribe(callback));

    return () => {
      callbacks.forEach((callback) => unsubscribe(callback));
    };
  }, [callbacks, subscribe, unsubscribe]);

  return { isAutoRefreshing, isManualRefreshing, isRefreshing };
};

export { RefreshContextProvider, useRefreshContext };
